import { Controller } from '@hotwired/stimulus'
import delay from 'lodash/delay'
import { getCount } from '../../api/common/count'

const DELAY_TIME = 800

export default class extends Controller {
  static targets = [
    'areaCheckbox',
    'checkboxAll',
    'checkboxes',
    'cityCheckbox',
    'cityCheckBoxesModalNextButton',
    'loading',
    'modal',
    'modalContainer',
    'prefectureAccordion',
    'region',
    'facilitiesCount',
    'footer',
    'main',
    'searchQuestionForm',
    'question',
    'submit',
    'submitButton',
    'feeNote',
    'chatInputFeeNextButton',
    'medicalCareList',
    'medicalCareOther',
    'chatInputMedicalCareNextButton',
    'summaryAboutAdmissionFee',
    'summaryAboutArea',
    'summaryAboutCareLevel',
    'summaryAboutDementia',
    'summaryAboutDoubleRoom',
    'summaryAboutWelfare',
    'resetButton',
  ]

  static classes = [
    'displayVisible',
    'displayHidden',
    'visibilityVisible',
  ]

  get controllerName () {
    return this.element.dataset.controller
  }

  get className () {
    return this.element.dataset.classname
  }

  connect () {
    this.setFacilitiesCount()
    this.showNextChat(1)
  }

  isElementInViewport (element) {
    const rect = element.getBoundingClientRect()
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }

  hideCurrentChatInput (e, text) {
    const title = e.currentTarget.dataset.title
    const current = e.currentTarget.dataset.current
    const currentInput = document.getElementById(`chat_input_${current}`)
    currentInput.classList.add(this.displayHiddenClass)

    delay(() => {
      currentInput.insertAdjacentHTML('beforebegin', `
        <div class="${this.element.dataset.classname}::chat-answer-container">
        <div class="${this.element.dataset.classname}::chat-answer">${text || title}</div>
        </div>
      `)
    }, DELAY_TIME / 2)

    delay(() => {
      const target = document.getElementById(`step_${current}`).getElementsByClassName(`${this.element.dataset.classname}::chat-answer-container`)
      if (!this.isElementInViewport(target[0])) {
        window.scroll({ top: target[0].offsetTop + target[0].offsetHeight - window.innerHeight + 30, left: 0, behavior: 'smooth' })
      }
    }, DELAY_TIME / 2 + 100)
  }

  showNextChat (step, delayTime) {
    const chatQuestionContainer = document.getElementById(`chat_question_container_${step}`)
    const chatInput = document.getElementById(`chat_input_${step}`)
    const loaders = chatQuestionContainer.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions = chatQuestionContainer.getElementsByClassName(`${this.className}::chat-question`)
    const nextDom = this.questionTargets[step - 1]

    this.questionTargets[step - 1].style.display = 'block'
    delay(() => {
      chatQuestionContainer.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME)
    Array.from(chatQuestions).forEach((elm, index) => {
      delay(() => {
        loaders[index].classList.add(this.displayHiddenClass)
        if (step !== 1 && step !== 2) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
        if (step === 2) window.scroll({ top: nextDom.offsetTop - 100, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 2) - 400)
      delay(() => {
        chatQuestions[index].classList.add(this.displayVisibleClass)
        if (index + 1 < chatQuestions.length) loaders[index + 1].classList.remove(this.displayHiddenClass)
      }, DELAY_TIME * (index + 2))
      delay(() => {
        if (step !== 1 && step !== 2) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 2))
    })
    if (delayTime) {
      delay(() => {
        chatInput.classList.add(this.visibilityVisibleClass)
      }, delayTime)
      delay(() => {
        if (step !== 1 && step !== 2) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, delayTime + 400)
    } else {
      delay(() => {
        chatInput.classList.add(this.visibilityVisibleClass)
        if (step !== 1 && step !== 2) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (chatQuestions.length + 2))
    }
  }

  showNextChatSlow (step) {
    const DELAY_TIME = 1000
    const chatQuestionContainer = document.getElementById(`chat_question_container_${step}`)
    const chatInput = document.getElementById(`chat_input_${step}`)
    const loaders = chatQuestionContainer.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions = chatQuestionContainer.getElementsByClassName(`${this.className}::chat-question`)
    const nextDom = this.questionTargets[step - 1]

    this.questionTargets[step - 1].style.display = 'block'
    delay(() => {
      chatQuestionContainer.classList.add(this.visibilityVisibleClass)
      window.scroll({ top: chatQuestionContainer.offsetTop + chatQuestionContainer.offsetHeight - window.innerHeight + 120, left: 0, behavior: 'smooth' })
    }, DELAY_TIME)
    Array.from(chatQuestions).forEach((elm, index) => {
      delay(() => {
        loaders[index].classList.add(this.displayHiddenClass)
      }, DELAY_TIME * (index + 2) - 400)
      delay(() => {
        chatQuestions[index].classList.add(this.displayVisibleClass)
      }, DELAY_TIME * (index + 2))
      delay(() => {
        window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 3))
    })
    delay(() => {
      chatInput.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME * (chatQuestions.length + 2))
    delay(() => {
      this.footerTarget.classList.add(this.displayVisibleClass)
    }, DELAY_TIME * (chatQuestions.length + 2) + 200)
  }

  nextChatWithScroll (e) {
    this.hideCurrentChatInput(e)
    this.showNextChat(Number(e.currentTarget.dataset.next))
  }

  skipChatWithScroll (e) {
    this.hideCurrentChatInput(e)
    this.showNextChat(Number(e.currentTarget.dataset.next) + 1)
  }

  chatInputMedicalCareNextButtonStatus (e) {
    const checkedFlag = [
      ...Array.from(document.querySelectorAll('input[name="medical_care_ids[]"]')),
      this.medicalCareOtherTarget,
    ].some(function (checkbox) {
      return checkbox.checked
    })
    this.chatInputMedicalCareNextButtonTarget.disabled = !checkedFlag
  }

  chatInputMedicalCareNextButtonStatusReset (e) {
    const checkboxes = this.element.parentNode.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
    })
    this.chatInputMedicalCareNextButtonStatus()
  }

  nextChatWithScrollForAdmissionFee (e) {
    const admissionFeeMin = document.getElementById('admission_fee_min')
    const admissionFeeMinText = admissionFeeMin.options[admissionFeeMin.selectedIndex].innerText
    const admissionFeeMax = document.getElementById('admission_fee_max')
    const admissionFeeMaxText = admissionFeeMax.options[admissionFeeMax.selectedIndex].innerText
    const admissionFeeText = `入居金${admissionFeeMinText}〜${admissionFeeMaxText}を希望します。`

    this.hideCurrentChatInput(e, admissionFeeText)
    this.showNextChat(Number(e.currentTarget.dataset.next))
  }

  nextChatWithScrollForMedicalCare (e) {
    const checkboxes = document.querySelectorAll('input[name="medical_care_ids[]"]')
    const medicalCareContent = Array.from(checkboxes).filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.dataset.title)
    const medicalCareText = medicalCareContent.length > 0 ? medicalCareContent.join('／') : 'その他（絞り込みなし）'
    this.hideCurrentChatInput(e, medicalCareText + 'です')
    this.showNextChat(Number(e.currentTarget.dataset.next))
  }

  openModal (e) {
    e.preventDefault()
    const currentIndex = this.regionTargets.indexOf(e.currentTarget)
    this.modalTarget.classList.remove(this.displayHiddenClass)
    this.prefectureAccordionTargets[currentIndex].classList.remove(this.displayHiddenClass)
  }

  toggle (e) {
    const target = e.target.parentNode

    if (target.classList.contains(`${this.className}::accordion-close`)) {
      this.open(target)
    } else {
      this.close(target)
    }
  }

  togglePref (e) {
    const target = e.target.parentNode

    if (target.classList.contains(`${this.className}::accordion-close`)) {
      this.open(target)
      setTimeout(() => {
        this.scrollToParent(target)
      }, 200)
    } else {
      this.close(target)
    }
  }

  open (target) {
    target.classList.remove(`${this.className}::accordion-close`)
    target.classList.add(`${this.className}::accordion-open`)
  }

  close (target) {
    target.classList.remove(`${this.className}::accordion-open`)
    target.classList.add(`${this.className}::accordion-close`)
  }

  scrollToParent (target) {
    const parentElement = this.modalContainerTarget
    const parentOffset = parentElement.getBoundingClientRect().top
    const childOffset = target.getBoundingClientRect().top
    const scrollTo = childOffset - parentOffset + parentElement.scrollTop

    parentElement.scroll({
      top: scrollTo,
      behavior: 'smooth',
    })
  }

  getPrefecture (e) {
    e.preventDefault()
    const { valuePref } = e.target.dataset
    const loading = this.loadingTargets.filter(element => element.dataset.valuePref === e.target.dataset.valuePref)
    const hasElement = this.cityCheckboxTargets.find(element => element.dataset.valuePref === valuePref)

    if (hasElement && !hasElement.innerHTML) {
      const params = new URLSearchParams()
      params.append('prefecture_ids[]', valuePref)
      const path = '/api/areas/cities/?' + params.toString()

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      const timeoutId = controller ? setTimeout(() => controller.abort(), 15000) : null

      fetch(path, controller ? { signal: controller.signal } : {})
        .then(data => {
          clearTimeout(timeoutId)
          return data.json()
        })
        .then(res => {
          if (loading[0]) this.hideLoading(loading[0])
          this.cities(res)
          this.scrollToParent(e.target)
        })
        .catch((error) => {
          clearTimeout(timeoutId)
          if (loading[0]) this.hideLoading(loading[0])
          console.error('市区町村の取得に失敗しました:', error)
        })
    }
  }

  cities (data) {
    // eslint-disable-next-line camelcase
    const { id, cities, ordinance_designated_cities } = data.prefectures[0]
    const prefectureId = String(id)
    const ordinanceDesignatedCitiesList = this.createOrdinanceDesignatedCitiesList(ordinance_designated_cities)
    const citiesList = this.createCityList(cities)
    const target = this.cityCheckboxTargets.find(element => element.dataset.valuePref === prefectureId)
    if (target && !target.innerHTML) {
      target.innerHTML += ordinanceDesignatedCitiesList + citiesList
    }

    if (target.previousElementSibling) {
      target.previousElementSibling.classList.remove(this.displayHiddenClass)
    }
  }

  createOrdinanceDesignatedCitiesList (ordinanceDesignatedCities) {
    const ordinanceDesignatedCityIdTargets = this.ordinanceDesignatedCityIdTargets
    return ordinanceDesignatedCities.map(ordinanceDesignatedCity => {
      return `
        <li class="${this.className}::checks">
            <label class="${this.className}::check">
                <input class="check-input" type="checkbox" value="${ordinanceDesignatedCity.id}" name="ordinance_designated_city_ids[]" data-text="${ordinanceDesignatedCity.name}" data-action="change->site--parts--search--cost--question--component#changeFormValue change->site--parts--search--cost--question--component#cityCheckBoxesModalNextButtonStatus data-site--parts--search--cost--question--component-target="ordinanceDesignatedCityList">
                <i></i>
                <span>${ordinanceDesignatedCity.name}</span>
            </label>
        </li>`
    }).join('')
  }

  createCityList (cities) {
    const cityIdTargets = this.cityIdTargets
    return cities.map((city) => {
      return `<li class="${this.className}::checks">
                  <label class="${this.className}::check">
                      <input class="check-input" type="checkbox" value="${city.id}" name="city_ids[]" data-text="${city.name}" data-action="change->site--parts--search--cost--question--component#changeFormValue change->site--parts--search--cost--question--component#cityCheckBoxesModalNextButtonStatus data-site--parts--search--cost--question--component-target="cityList">
                      <i></i>
                      <span>${city.name}</span>
                  </label>
              </li>`
    }).join('')
  }

  hideLoading (target) {
    target.style.display = 'none'
  }

  cityCheckBoxesModalNextButtonStatus (e) {
    const checkedFlag = [
      ...Array.from(document.querySelectorAll('input[name="ordinance_designated_city_ids[]"]')),
      ...Array.from(document.querySelectorAll('input[name="city_ids[]"]')),
    ].some(function (checkbox) {
      return checkbox.checked
    })
    this.cityCheckBoxesModalNextButtonTarget.disabled = !checkedFlag
  }

  checkAll (e) {
    const currentIndex = this.checkboxAllTargets.indexOf(e.currentTarget)
    const checkboxes = this.checkboxesTargets[currentIndex].querySelectorAll('input[type="checkbox"]')
    const allCheckbox = e.currentTarget
    const isChecked = allCheckbox.checked

    function updateCheckboxes (isChecked) {
      checkboxes.forEach((checkbox) => {
        if (checkbox.checked !== isChecked) {
          checkbox.checked = isChecked
        }
      })
    }

    updateCheckboxes(isChecked)

    this.cityCheckBoxesModalNextButtonStatus()
  }

  nextChatWithScrollForArea (e) {
    this.modalTarget.classList.add(this.displayHiddenClass)
    const areaText = this.joinAreaNames(this.areaContent()) ? this.joinAreaNames(this.areaContent()) + 'を希望します。' : null
    this.hideCurrentChatInput(e, areaText)
    this.showNextChat(Number(e.currentTarget.dataset.next))
  }

  areaContent () {
    const checkboxesForOrdinanceDesignatedCity = document.querySelectorAll('input[name="ordinance_designated_city_ids[]"]')
    const checkboxesForCity = document.querySelectorAll('input[name="city_ids[]"]')
    const ordinanceDesignatedCityContent = Array.from(checkboxesForOrdinanceDesignatedCity).filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.dataset.text)
    const cityContent = Array.from(checkboxesForCity).filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.dataset.text)
    return ordinanceDesignatedCityContent.concat(cityContent)
  }

  joinAreaNames (areas) {
    if (areas.length > 3) {
      return areas.slice(0, 3).join('/') + '...'
    } else if (areas.length > 0) {
      return areas.join('/')
    } else {
      return null
    }
  }

  changeFormValue (e) {
    this.setFacilitiesCount()
  }

  displaySummary (e) {
    this.hideCurrentChatInput(e)
    this.setfacilitiesCountText(Number(e.currentTarget.dataset.next))
    this.setSelectedContentList()
    this.resetButtonTarget.style.display = 'none'
  }

  setSelectedContentList () {
    this.setSelcetedCareLevel()
    this.setSelcetedDementia()
    this.setSelectedWelfare()
    this.setSelcetedArea()
    this.setSelectCost()
    // this.changeFeatureContentTagColor()
  }

  setFacilitiesCount (e) {
    if (typeof FormData !== 'function') {
      return
    }

    getCount(this.facilitiesCountParams()).then((count) => {
      const countElement = this.submitTargets[0]
      const startNumber = parseInt(countElement.textContent)
      const endNumber = count.result

      this.submitTargets.forEach((element) => {
        if (count.result >= 10000) {
          element.innerText = '10000+'
        } else if (count.result <= 0) {
          element.innerText = count.result
          window.gtagEvent('step', 'inqueryCategory:cost:question', 'form_0_facilities')
        } else {
          element.innerText = count.result
          this.animateCount(countElement, startNumber, endNumber)
        }
      })
    }).catch((error) => {
      // APIエラー時は操作を継続可能にする
      console.error('Failed to fetch facilities count:', error)
    })
  }

  facilitiesCountParams () {
    const formData = new FormData(this.searchQuestionFormTarget)
    const params = {
      city_ids: formData.getAll('city_ids[]').filter(value => value !== ''),
      ordinance_designated_city_ids: formData.getAll('ordinance_designated_city_ids[]').filter(value => value !== ''),
      monthly_fee_min: formData.get('monthly_fee_min'),
      monthly_fee_max: formData.get('monthly_fee_max'),
      admission_fee_min: formData.get('admission_fee_min'),
      admission_fee_max: formData.get('admission_fee_max'),
      care_insurance_rate: formData.get('care_insurance_rate'),
      care_level_id: formData.get('care_level_id'),
      dementia_acceptable: formData.get('dementia_acceptable'),
      medical_care_ids: formData.getAll('medical_care_ids[]').filter(value => value !== ''),
      welfare_acceptable: formData.get('welfare_acceptable'),
      feature_content_ids: formData.getAll('feature_content_ids[]').filter(value => value !== ''),
    }

    return params
  }

  animateCount (element, start, end) {
    let startTime = null

    const updateNumber = (timestamp) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / 200, 1)
      const currentNumber = Math.floor(start + (end - start) * progress)
      element.textContent = currentNumber

      if (progress < 1) {
        requestAnimationFrame(updateNumber)
      }
    }

    requestAnimationFrame(updateNumber)
  }

  areaIds (ids) {
    if (!ids) return

    if (Array.from(ids).length > 1) {
      return Array.from(ids).map((id) => id.value)
    } else {
      return Array(ids).map((id) => id.value)
    }
  }

  setfacilitiesCountText (target) {
    getCount(this.facilitiesCountParams()).then((count) => {
      if (count.result > 0) {
        this.facilitiesCountTarget.insertAdjacentHTML('afterbegin', `
          <span>これまで入力いただいた内容で該当する施設は</span>
          <em class="chat-question-em">${count.result}</em>
          <span>件です。</span>
        `)
      } else {
        this.facilitiesCountTarget.insertAdjacentHTML('afterbegin', `
          <span>これまでご入力いただいた内容で該当する施設はございませんでした。</span>
          <br>
          <span>「条件を緩和する」を押して、条件編集ボタンから条件を緩和してみましょう。</span>
        `)
        this.submitButtonTarget.value = '条件を緩和する'
      }
      this.showNextChatSlow(target)
    }).catch((error) => {
      // APIエラー時も次のステップに進めるようにする
      console.error('Failed to fetch facilities count:', error)
      this.showNextChatSlow(target)
    })
  }

  setSelcetedArea () {
    const summaryText = this.joinAreaNames(this.areaContent()) || 'なし'
    this.summaryAboutAreaTarget.insertAdjacentHTML('afterbegin', `
      <div class="${this.element.dataset.classname}::summary-text">${summaryText}</div>
    `)
  }

  setSelcetedCareLevel () {
    const careLevelIdInput = document.querySelector('input[name="care_level_id"]:checked')
    if (careLevelIdInput) {
      const summaryText = careLevelIdInput.dataset.summaryText
      this.summaryAboutCareLevelTarget.insertAdjacentHTML('afterbegin', `
        <div class="${this.element.dataset.classname}::summary-text">${summaryText}/</div>
      `)
    }
  }

  setSelcetedDementia () {
    const dementiaInput = document.querySelector('input[name="dementia_acceptable"]:checked')
    const summaryText = dementiaInput ? dementiaInput.dataset.summaryText : '認知症なし'
    this.summaryAboutDementiaTarget.insertAdjacentHTML('afterbegin', `
      <div class="${this.element.dataset.classname}::summary-text">${summaryText}/</div>
    `)
  }

  setSelectedWelfare () {
    const welfareIdInput = document.querySelector('input[name="welfare_acceptable"]:checked')
    const summaryText = welfareIdInput ? welfareIdInput.dataset.summaryText : '生活保護なし'
    this.summaryAboutWelfareTarget.insertAdjacentHTML('afterbegin', `
      <div class="${this.element.dataset.classname}::summary-text">${summaryText}</div>
    `)
  }

  setSelectCost () {
    const admissionFeeMin = document.getElementById('admission_fee_min')
    const admissionFeeMinText = admissionFeeMin.options[admissionFeeMin.selectedIndex].innerText
    const admissionFeeMax = document.getElementById('admission_fee_max')
    const admissionFeeMaxText = admissionFeeMax.options[admissionFeeMax.selectedIndex].innerText
    this.summaryAboutAdmissionFeeTarget.insertAdjacentHTML('afterbegin', `
      <div class="${this.element.dataset.classname}::summary-text">${admissionFeeMinText}~${admissionFeeMaxText}</div>
    `)
  }

  changeFeatureContentTagColor () {
    const featureContentInputs = document.querySelectorAll('input[name="feature_content_ids[]"]:checked')
    const featureContentIds = Array.from(featureContentInputs).map((featureContent) => featureContent.value)
    this.changeNursingTagColor(featureContentIds)
    this.changeRoomTypeTagColor(featureContentIds)
    this.changeRecreationTagColor(featureContentIds)
  }

  changeNursingTagColor (featureContentIds) {
    if (featureContentIds.length === 0) return

    const twentyFourHourNursingId = '103'
    if (featureContentIds.includes(twentyFourHourNursingId)) {
      this.summaryAboutNursingTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutNursingTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    }
  }

  changeRoomTypeTagColor (featureContentIds) {
    const privateRoomId = '201'
    const doubleRoomId = '202'

    if (featureContentIds.includes(privateRoomId)) {
      this.summaryAboutPrivateRoomTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutPrivateRoomTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    } else if (featureContentIds.includes(doubleRoomId)) {
      this.summaryAboutDoubleRoomTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutDoubleRoomTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    } else {
      this.summaryAboutNormalRoomTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutNormalRoomTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    }
  }

  changeRecreationTagColor (featureContentIds) {
    if (featureContentIds.length === 0) return

    const recreationId = '401'
    if (featureContentIds.includes(recreationId)) {
      this.summaryAboutRecreationTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutRecreationTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    }
  }
}
